import { Controller } from "@hotwired/stimulus"

const CHECKMARK_SVG = '<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="3" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>'

export default class extends Controller {
  static targets = ["stepCircle", "stepLabel", "stepConnector", "subdomainInput"]
  static values = { step: { type: Number, default: 1 }, googleSignupUrl: String }

  connect() {
    this.subdomainManuallyEdited = false
    this.stepCache = new Map()
    this.updateIndicator()
  }

  // Cache current step DOM before form submission (preserves user input)
  cacheStep() {
    const frame = this.element.querySelector("#signup_step")
    if (!frame) return

    // Sync live form values to DOM attributes so innerHTML captures them
    frame.querySelectorAll("input, select, textarea").forEach(el => {
      if (el.type === "checkbox" || el.type === "radio") {
        if (el.checked) el.setAttribute("checked", "")
        else el.removeAttribute("checked")
      } else if (el.tagName === "SELECT") {
        el.querySelectorAll("option").forEach(opt => {
          if (opt.selected) opt.setAttribute("selected", "")
          else opt.removeAttribute("selected")
        })
      } else {
        el.setAttribute("value", el.value)
      }
    })

    this.stepCache.set(this.stepValue, frame.innerHTML)
  }

  // Fired by turbo:frame-load on the turbo frame after content replacement
  onStepLoad() {
    const frame = this.element.querySelector("#signup_step")
    const stepInput = frame?.querySelector("input[name='step']")
    if (stepInput) {
      const newStep = parseInt(stepInput.value)
      if (newStep !== this.stepValue) {
        this.stepValue = newStep
      }
    }
  }

  // Restore cached previous step (no server round trip)
  goBack() {
    const prevStep = this.stepValue - 1
    if (prevStep >= 1 && this.stepCache.has(prevStep)) {
      const frame = this.element.querySelector("#signup_step")
      frame.innerHTML = this.stepCache.get(prevStep)
      this.stepValue = prevStep
    }
  }

  stepValueChanged() {
    this.updateIndicator()
  }

  updateIndicator() {
    const index = this.stepValue - 1

    this.stepCircleTargets.forEach((el, i) => {
      if (i < index) {
        el.className = "flex items-center justify-center w-8 h-8 rounded-full bg-black text-white text-xs font-semibold transition-all duration-300"
        el.innerHTML = CHECKMARK_SVG
      } else if (i === index) {
        el.className = "flex items-center justify-center w-8 h-8 rounded-full bg-black text-white text-xs font-semibold transition-all duration-300"
        el.textContent = i + 1
      } else {
        el.className = "flex items-center justify-center w-8 h-8 rounded-full bg-white border-2 border-gray-200 text-gray-400 text-xs font-semibold transition-all duration-300"
        el.textContent = i + 1
      }
    })

    this.stepLabelTargets.forEach((el, i) => {
      if (i <= index) {
        el.classList.remove("text-gray-400")
        el.classList.add("text-black")
      } else {
        el.classList.remove("text-black")
        el.classList.add("text-gray-400")
      }
    })

    this.stepConnectorTargets.forEach((el, i) => {
      if (i < index) {
        el.classList.remove("bg-gray-200")
        el.classList.add("bg-black")
      } else {
        el.classList.remove("bg-black")
        el.classList.add("bg-gray-200")
      }
    })
  }

  // Build and submit a form to Google signup, bypassing Turbo Frame
  submitGoogleSignup() {
    const frame = this.element.querySelector("#signup_step")
    if (!frame) return

    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.googleSignupUrlValue
    form.style.display = "none"

    const token = document.querySelector("meta[name='csrf-token']")?.content
    if (token) this._addField(form, "authenticity_token", token)

    // Collect all hidden fields (org data) + visible field values from the frame
    const fields = {
      "organization[name]": frame.querySelector("[name='organization[name]']")?.value,
      "organization[business_type]": frame.querySelector("[name='organization[business_type]']")?.value,
      "organization[subdomain]": frame.querySelector("[name='organization[subdomain]']")?.value,
      "user[name]": frame.querySelector("[name='user[name]']")?.value,
      "user[phone]": frame.querySelector("[name='user[phone]']")?.value,
      "user[email]": frame.querySelector("[name='user[email]']")?.value
    }

    for (const [name, value] of Object.entries(fields)) {
      this._addField(form, name, value || "")
    }

    document.body.appendChild(form)
    form.submit()
  }

  _addField(form, name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  // Subdomain auto-fill from business name
  updateSubdomain() {
    if (this.subdomainManuallyEdited) return
    if (!this.hasSubdomainInputTarget) return

    const name = this.element.querySelector("[name='organization[name]']")?.value
    if (name) {
      this.subdomainInputTarget.value = this.sanitizeSubdomain(name)
    }
  }

  sanitizeSubdomainInput() {
    this.subdomainManuallyEdited = true
    const input = this.subdomainInputTarget
    input.value = this.sanitizeSubdomain(input.value)
  }

  sanitizeSubdomain(value) {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^[-_]+|[-_]+$/g, "")
  }
}
